import IProcessed_Report_Detail from "@models/IProcessed_Report_Detail";
import IPriceGapReportRow from "@models/IPriceGapReportRow";
import PriceGapStatusEnum from "@models/PriceGapStatusEnum";

/**
 * CSS class for a Price Gap status cell. Undercharging and price-set mismatches are the money-losing cases,
 * so they share the strongest emphasis.
 */
export const getStatusClass = (status: PriceGapStatusEnum | string): string => {
    switch (status) {
        case PriceGapStatusEnum.NO_PRICE_UNDERCHARGING:
        case PriceGapStatusEnum.PRICE_SET_MISMATCH:
            return "status-undercharging";

        case PriceGapStatusEnum.NO_PRICE_OVERCHARGING:
            return "status-overcharging";

        case PriceGapStatusEnum.NO_PRICE_NO_MSRP:
            return "status-no-msrp";

        case PriceGapStatusEnum.INCONSISTENT_HISTORY:
            return "status-inconsistent";

        case PriceGapStatusEnum.OK:
            return "status-ok";

        default:
            return "";
    }
};

/**
 * Translation key describing what a status means. Mirrors the classification precedence documented in the
 * API's PriceGapReportService.classifyRow.
 */
export const getStatusDescriptionKey = (status: PriceGapStatusEnum | string): string => {
    switch (status) {
        case PriceGapStatusEnum.NO_PRICE_NO_MSRP:
            return "price_gap_status_no_price_no_msrp";

        case PriceGapStatusEnum.NO_PRICE_UNDERCHARGING:
            return "price_gap_status_no_price_undercharging";

        case PriceGapStatusEnum.NO_PRICE_OVERCHARGING:
            return "price_gap_status_no_price_overcharging";

        case PriceGapStatusEnum.PRICE_SET_MISMATCH:
            return "price_gap_status_price_set_mismatch";

        case PriceGapStatusEnum.INCONSISTENT_HISTORY:
            return "price_gap_status_inconsistent_history";

        case PriceGapStatusEnum.OK:
            return "price_gap_status_ok";

        default:
            return "";
    }
};

/**
 * Price Gap rows are stored one per Processed_Report_Detail with the row payload under `detail_data.priceGap`.
 * Flattens them for the table, dropping any detail without a payload, and keeps the server order (rank).
 */
export const flattenPriceGapRows = (details: IProcessed_Report_Detail[]): IPriceGapReportRow[] => {
    const rows: IPriceGapReportRow[] = [];

    for (const detail of details || []) {
        const row = detail.detail_data?.priceGap;

        if (row) {
            rows.push(row);
        }
    }

    return rows;
};
